import { memo, useMemo } from 'react'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'
import { Box } from '@machobear/design-system'
import noop from 'lodash/noop'
import { useFeatureFlagVariantKey } from 'posthog-js/react'

import structure from '@/structure'
import { breakpoints, colors, fontSizes } from '@/styles'
import { isEmptyOrNull } from '@/utils'
import { useHeaderContext } from '@/hooks/use-header-context'
import useMediaQuery from '@/hooks/use-media-query'
import EmptyState from '@/features/Listings/empty-state'
import useListingsFavourites from '@/features/Listings/hooks/use-listings-favourites'
import { SearchResultsContent } from '@/features/Listings/seo'
import { CityBreadcrumbs } from '@/components/breadcrumbs'
import { useListImpressionTracking } from '@/components/ImpressionProvider/useListImpressionTracking'
import Pagination from '@/components/pagination'
import SearchTitle from '@/components/search-title'
import { ParagraphRegular } from '@/components/text'

import { useMapList } from '../maplist-context'
import FeaturedListings from './featured-listings'
import RegularListings from './regular-listings'
import RegularListingsV2 from './regular-listings-v2'

const ListWrapper = styled.div`
  flex: 1;
  width: 100%;
  display: grid;
  align-content: start;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    overflow-x: hidden;
    flex: 0 1 auto;
    height: ${props => {
      if (props.$isCampus) {
        const activeFilterHeight = props.$isFilterSet
          ? structure.mobile.activeFilters
          : 0

        return `calc(100dvh - ${structure.mobile.header}px - ${structure.mobile.searchWrapper}px - ${structure.mobile.actions}px - ${activeFilterHeight}px)`
      } else {
        return `calc(100dvh - ${structure.mobile.footer}px)`
      }
    }};
    padding-top: ${props => {
      if (props.$isCampus) {
        return `0px`
      }

      if (props.$isFilterSet) {
        return `calc(${structure.mobile.header}px + ${structure.mobile.searchWrapper}px +
        ${structure.mobile.actions}px + ${structure.mobile.activeFilters}px)`
      } else {
        return `calc(${structure.mobile.header}px + ${structure.mobile.searchWrapper}px +
        ${structure.mobile.actions}px)`
      }
    }};
  }
`

const SearchTitleWrapper = styled.div`
  padding: 24px 20px 0;
`

const Content = styled.div`
  padding: 28px 20px 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: ${structure.map.listWidthTriple}px;

  @media (max-width: ${breakpoints.mapLarge}) {
    width: ${structure.map.listWidthDouble}px;
  }

  @media (max-width: ${breakpoints.map}) {
    width: ${structure.map.listWidthSingle}px;
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100vw;
    max-width: 100vw;
    box-sizing: border-box;
    padding: 24px 16px 30px;
    overflow-x: hidden;
  }
`

const NearbyListingsText = styled(ParagraphRegular)`
  font-weight: 500;
`

const PaginationWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;

  @media (max-width: ${breakpoints.map}) {
    max-width: 325px;
    margin: 0 auto;
  }
`

const PaginationSummary = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 50px;
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: 600;
  letter-spacing: -0.2px;
  line-height: 22px;
`

const NoResultsWrapper = styled.div`
  padding: 0 20px;
  width: ${structure.map.listWidthTriple}px;
  @media (max-width: ${breakpoints.mapLarge}) {
    width: ${structure.map.listWidthDouble}px;
  }
  @media (max-width: ${breakpoints.map}) {
    width: ${structure.map.listWidthSingle}px;
  }
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100vw;
  }
`

const pageButtonsStyle = css`
  @media (max-width: ${breakpoints.map}) {
    margin: 0 2px;
  }
`

const List = memo(
  ({
    onlyVerifiedListings,
    setHighlightedBuilding = noop,
    preload = noop,
    markersLoaded,
    setVisitedBuilding,
    customTitle,
    customBreadcrumbs,
    hideFeaturedListings,
    hideSeoContent,
    isCampus,
    metadata,
    buildings,
    buildingsWithAds,
    loadingBuildings,
    featured,
    data,
    neighborhood,
    city,
    cityLoading,
    citySlug,
    filterSlug,
    filter
  }) => {
    const { t } = useTranslation()
    const isMobile = useMediaQuery(`(max-width: ${breakpoints.map})`)
    useListImpressionTracking({ id: 'map-listings', minVisibleDuration: 0 })
    useListingsFavourites()
    const router = useRouter()
    const isChinese = router.locale === 'zh'

    const { listWrapperRef } = useMapList()
    const { scrollTriggerRef, isHeaderVisible } = useHeaderContext()

    const variant = useFeatureFlagVariantKey('search_algorithm_v2')
    const isV2 = !isCampus && variant === 'v2'

    const listingsCount = metadata.listings_count
    const buildingsCount = metadata.total_count

    const totalPages = Math.ceil(buildingsCount / metadata.page_size)

    const firstItem = (metadata.page - 1) * metadata.page_size + 1
    const lastItem = Math.min(
      metadata.page * metadata.page_size,
      buildingsCount
    )

    const hasCityData = !isEmptyOrNull(city)
    const hasNeighborhoodData = !isEmptyOrNull(neighborhood.data)
    const cityName = city?.translated
    const hoodName = neighborhood?.data?.translated
    const displayName = neighborhood?.data?.translated || city?.translated
    const stateName = city?.translatedState

    const nameWithState = isChinese
      ? `${city.translatedState}${city.translated}`
      : `${city.translated}, ${city.translatedState}`
    const cityNameWithState = hasCityData ? nameWithState : t('seo:Canada')

    const customTitleValue = useMemo(() => {
      if (customTitle) return customTitle
      if (hasNeighborhoodData) {
        const isPoiPage = neighborhood?.data?.type === 'poi'
        const isUniversityPage = neighborhood?.data?.type === 'university'

        if (filterSlug) {
          if (isPoiPage || isUniversityPage) {
            return `${t(`seo:filter_title_${filterSlug}`, {
              defaultValue: '{{ cityName }}',
              cityName: cityNameWithState
            })} ${t(`seo.pois:title_near_location`, {
              defaultValue: 'near {{ locationName }}',
              locationName: hoodName
            })}`
          }
          return t(`seo:filter_title_${filterSlug}`, {
            defaultValue: '{{ cityName }}',
            cityName: `${hoodName}, ${cityNameWithState}`
          })
        }
        if (isPoiPage) {
          return (
            <Trans
              i18nKey="seo:search_title_near_neighborhood_city_state"
              values={{ hoodName, cityName, stateName }}
              defaults="Apartments, Condos and Houses for Rent near {{ hoodName }}, {{ cityName }}, {{ stateName}}"
            />
          )
        }
        if (isUniversityPage) {
          return hasCityData ? (
            <Trans
              i18nKey="seo:search_title_near_neighborhood_city_state"
              values={{ hoodName, cityName, stateName }}
              defaults="Apartments, Condos and Houses for Rent near {{ hoodName }}, {{ cityName }}, {{ stateName}}"
            />
          ) : (
            <Trans
              i18nKey="seo:search_title_near_neighborhood"
              values={{ hoodName }}
              defaults="Apartments, Condos and Houses for Rent near {{ hoodName }}"
            />
          )
        }
        return hasCityData ? (
          <Trans
            i18nKey="seo:search_title_in_neighborhood_city_state"
            values={{ hoodName, cityName, stateName }}
            defaults="Apartments, Condos and Houses for Rent in {{ hoodName }}, {{ cityName }}, {{ stateName}}"
          />
        ) : (
          <Trans
            i18nKey="seo:search_title_in_neighborhood"
            values={{ hoodName }}
            defaults="Apartments, Condos and Houses for Rent in {{ hoodName }}"
          />
        )
      }

      if (filterSlug) {
        return t(`seo:filter_title_${filterSlug}`, {
          defaultValue: '{{ cityName }}',
          cityName: cityNameWithState
        })
      }
      return undefined
    }, [
      customTitle,
      neighborhood?.data,
      filterSlug,
      cityName,
      cityNameWithState,
      hasCityData,
      hasNeighborhoodData,
      hoodName,
      stateName,
      t
    ])

    const handlePageChange = ({ currentPage }) => {
      if (currentPage === metadata.page) return
      filter.setFilter({ page: currentPage })
      listWrapperRef.current.scrollTop = 0
      window.scrollTo({ top: 0, behavior: 'auto' })
    }

    const hasPagination = buildingsCount > metadata.page_size

    const renderedListings = isCampus ? buildings : buildingsWithAds

    return (
      <ListWrapper
        $isFilterSet={filter?.isFilterSet}
        ref={node => {
          listWrapperRef.current = node
          scrollTriggerRef.current = node
        }}
        $isHeaderVisible={isHeaderVisible}
        $isCampus={isCampus}
      >
        <Box data-impression-container="true">
          <SearchTitleWrapper>
            {customBreadcrumbs ? (
              customBreadcrumbs
            ) : citySlug ? (
              <CityBreadcrumbs
                cityData={city}
                neighborhoodData={neighborhood?.data}
                loading={cityLoading || neighborhood?.isLoading}
                filterSlug={filterSlug}
              />
            ) : null}
            <SearchTitle
              cityName={displayName}
              CustomTitle={customTitleValue}
              loadingCount={loadingBuildings || !markersLoaded}
              listingCount={listingsCount}
              isMapMode
              showSort
            />
          </SearchTitleWrapper>
          {renderedListings?.length < 1 && !isEmptyOrNull(data) ? (
            <NoResultsWrapper>
              <EmptyState isCampus={isCampus} />
            </NoResultsWrapper>
          ) : (
            <>
              <Content hasPagination={hasPagination}>
                {markersLoaded && !listingsCount && !loadingBuildings && (
                  <NearbyListingsText>
                    {hasCityData
                      ? t('b.listings.view.explore_nearby_listings.text', {
                          defaultValue:
                            'Explore listings near {{ cityName }} that match your search:',
                          cityName: displayName
                        })
                      : t(
                          'b.listings.view.explore_nearby_listings_general.text',
                          'Explore listings nearby that match your search:'
                        )}
                  </NearbyListingsText>
                )}
                {isV2 ? (
                  <RegularListingsV2
                    listings={renderedListings}
                    loading={loadingBuildings}
                    setHighlightedBuilding={setHighlightedBuilding}
                    setVisitedBuilding={setVisitedBuilding}
                    hideFeaturedListings
                    onlyVerifiedListings={onlyVerifiedListings}
                    isMobile={isMobile}
                  />
                ) : (
                  <>
                    {!hideFeaturedListings && (
                      <FeaturedListings
                        loading={loadingBuildings}
                        listings={featured}
                        setHighlightedBuilding={setHighlightedBuilding}
                        setVisitedBuilding={setVisitedBuilding}
                      />
                    )}
                    <RegularListings
                      listings={renderedListings}
                      loading={loadingBuildings}
                      setHighlightedBuilding={setHighlightedBuilding}
                      setVisitedBuilding={setVisitedBuilding}
                      hideFeaturedListings={hideFeaturedListings}
                      onlyVerifiedListings={onlyVerifiedListings}
                      isMobile={isMobile}
                    />
                  </>
                )}
              </Content>
              {hasPagination && (
                <PaginationWrapper>
                  <Pagination
                    currentPage={metadata.page}
                    totalRecords={buildingsCount}
                    pageLimit={metadata.page_size}
                    totalPages={totalPages}
                    pageNeighbours={1}
                    onPageChanged={handlePageChange}
                    preload={preload}
                    customStyle={pageButtonsStyle}
                  />
                  <PaginationSummary>
                    {firstItem} - {`${lastItem} `}
                    <Trans
                      i18nKey="b.listings.view.position_in_buildings.text"
                      values={{ totalBuildings: buildingsCount }}
                      defaults="of {{ totalBuildings }} buildings"
                    />
                  </PaginationSummary>
                </PaginationWrapper>
              )}
            </>
          )}
          {!hideSeoContent && (
            <SearchResultsContent
              cityData={city}
              neighborhoodData={neighborhood?.data}
              metadata={metadata}
              filterSlug={filterSlug}
            />
          )}
        </Box>
      </ListWrapper>
    )
  }
)

List.displayName = 'List'

export { List }
